import React from 'react';
import {
    FilterButton,
    List,
    SearchInput,
    TopToolbar,
    TextField,
    DateField,
    DatagridConfigurable,
    SelectColumnsButton,
    useTranslate,
    FunctionField,
    Title,
    useNotify,
    WithListContext,
    DatagridLoading,
    Pagination,
    useListContext,
    downloadCSV,
} from 'react-admin';
import { useMediaQuery, Theme, Stack } from '@mui/material';
import SchoolMergeButton from '../SchoolMergeButton';
import { Alert } from '@mui/material';
import TeacherListAside from './TeacherListAside';
// import ProductFullNameField from '../product/ProductFullNameField';
// import SubscriberFullNameField from './SubscriberFullNameField';
import CustomLogAsTeacherButton from '../../controls/CustomLogAsTeacherButton';
import CustomCopyAccessTokenButton from '../../controls/CustomCopyAccessTokenButton';
import CustomPagination from '../../common/CustomPagination';
import SourceNameField from '../SourceNameField';
import SessionBadge from '../controls/SessionBadge';
import StaleBadge from '../controls/StaleBadge';
import ConditionalLoginField from '../ConditionalLoginField';
import ConditionalRegisteredField from '../ConditionalRegisteredField';
import SchoolNameField from '../SchoolNameField';
import ValidityStatusBadge from '../ValidityStatusBadge';
import CreateUserDialogButton from '../CreateUserDialogButton';
import BulkResendInvitationButton from '../BulkResendInvitationButton';
import BulkForceStaleButton from '../BulkForceStaleButton';
import { BulkDeleteButton } from 'react-admin';
import { IfCanAccess } from '@react-admin/ra-rbac';
import { useCanAccess } from '@react-admin/ra-rbac';
import jsonExport from 'jsonexport/dist';

const teacherFilters = [<SearchInput source="q" alwaysOn />];

const TeacherBulkActions = () => (
    <>
        <BulkResendInvitationButton />
        <BulkForceStaleButton />
        <BulkDeleteButton />
    </>
);

const ListActions = () => {
    const { filterValues } = useListContext();
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    return (
        <TopToolbar>
            {isSmall ? <FilterButton /> : null}

            <SchoolMergeButton
                resource="teacher"
                school={filterValues?.parent_group}
            />

            <IfCanAccess action="create" resource="teacher">
                <CreateUserDialogButton resource="teacher" />
            </IfCanAccess>
            {/* Specify the resource prop if required */}
            {/* <ListViewButton /> */}
            <SelectColumnsButton preferenceKey="teacher.datagrid.v4" />
        </TopToolbar>
    );
};

const TeacherList = () => {
    const notify = useNotify();
    const { canAccess: canWriteTeacher } = useCanAccess({
        action: 'write',
        resource: 'teacher',
    });
    const handleOnClick = e => {
        e.stopPropagation();
        navigator.clipboard.writeText(e.target.innerText);
        //notify(e.target.innerText + ' kopioitu leikepöydälle.', { type: 'info' });
        notify(
            <Alert severity="info">
                {e.target.innerText} kopioitu leikepöydälle.
            </Alert>
        );
    };

    const isXsmall = useMediaQuery<Theme>(theme =>
        theme.breakpoints.down('sm')
    );
    const sortable = [
        'public.firstname',
        'public.lastname',
        'username',
        'last_login',
        'registered',
    ];

    const translate = useTranslate();
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    const exporter = teachers => {
        const rows = teachers.map(teacher => ({
            id: teacher.id,
            Etunimi: teacher.public?.firstname,
            Sukunimi: teacher.public?.lastname,
            Sähköposti: teacher.public?.email,
            Käyttäjätunnus: teacher.username,
            'Viimeisin kirjautuminen': teacher.last_login || '',
            Rekisteröity: teacher.registered,
            Oppilaitokset:
                teacher.schools?.map(school => school.name).join(', ') || '',
        }));
        jsonExport(
            rows,
            {
                headers: [
                    'id',
                    'Etunimi',
                    'Sukunimi',
                    'Sähköposti',
                    'Käyttäjätunnus',
                    'Viimeisin kirjautuminen',
                    'Rekisteröity',
                    'Oppilaitokset',
                ],
                rowDelimiter: ';',
            },
            (exportError, csv) => {
                if (exportError) {
                    console.error('Export error:', exportError);
                    notify(translate('notifications.EXPORT_ERROR'), {
                        type: 'error',
                    });
                    return;
                }
                downloadCSV(`\ufeff${csv}`, 'users');
            }
        );
    };

    return (
        <List
            filters={isSmall ? teacherFilters : undefined}
            sort={{ field: 'registered', order: 'DESC' }}
            perPage={50}
            pagination={
                <Pagination rowsPerPageOptions={[10, 25, 50, 100, 200]} />
            }
            aside={<TeacherListAside />}
            actions={<ListActions />}
            filter={{
                need_count: true,
                sessions: true,
                licenses: false,
                stale_info: true,
            }}
            title={<Title title="My Custom Page" />}
            exporter={exporter}
            //pagination={false ? <CustomPagination /> : undefined}
        >
            <WithListContext
                render={({ isLoading, isFetching, data }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={8} nbFakeLines={8} />
                    ) : (
                        <DatagridConfigurable
                            preferenceKey="teacher.datagrid.v4"
                            /*
                            rowSx={record => ({
                                color:
                                    record.status === 0
                                        ? 'none'
                                        : 'rgba(255, 0, 0, 0.05)',
                            })}
                            */
                            //optimized
                            omit={[
                                'guid',
                                'copy_access_token',
                                'public.validfrom',
                                'public.validto',
                            ]}
                            rowClick="edit"
                            //rowClick={(id: any, basePath: any, record: any) => {window.open("https://oppimateriaalit-aspa.otava.fi/#/"+basePath+"/"+id, "_blank");return false;}}
                            bulkActionButtons={
                                canWriteTeacher ? <TeacherBulkActions /> : false
                            }
                            sx={{
                                '& .column-groups': {
                                    md: { display: 'none' },
                                    lg: { display: 'table-cell' },
                                },
                            }}
                        >
                            {/* {columns} */}
                            <TextField
                                source="guid"
                                key="guid"
                                sortable={sortable.includes('guid')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.firstname"
                                key="firstname"
                                sortable={sortable.includes('public.firstname')}
                                onClick={handleOnClick}
                            />
                            <TextField
                                source="public.lastname"
                                key="lastname"
                                sortable={sortable.includes('public.lastname')}
                                onClick={handleOnClick}
                            />
                            <StaleBadge
                                label="resources.teacher.fields.username_column"
                            >
                                <FunctionField
                                    source="username"
                                    label="resources.teacher.fields.username_column"
                                    sortable={sortable.includes('username')}
                                    render={record => (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            <TextField
                                                source="username"
                                                key="username"
                                                onClick={handleOnClick}
                                            />
                                            {(record?.public?.validfrom ||
                                                record?.public?.validto) && (
                                                <ValidityStatusBadge
                                                    record={record}
                                                    statusSource="status"
                                                    validFromSource="public.validfrom"
                                                    validToSource="public.validto"
                                                    statusResourceKey="resources.teacher.status"
                                                    variant="caption"
                                                />
                                            )}
                                        </Stack>
                                    )}
                                />
                            </StaleBadge>
                            <SchoolNameField
                                source="school.title"
                                key="school"
                                approval="true"
                                sortable={sortable.includes('school')}
                            />

                            <SessionBadge
                                source="last_login"
                                options={{
                                    showTTL: false,
                                }}
                                sortByOrder="DESC"
                            >
                                <ConditionalLoginField
                                    showTime
                                    locales="fi-FI"
                                    sortable={sortable?.includes('last_login')} // Conditional sortable
                                    sortByOrder="DESC"
                                    allConditions="false"
                                />
                            </SessionBadge>

                            <ConditionalRegisteredField
                                source="registered"
                                key="registered"
                                sortable={sortable.includes('registered')}
                                sortByOrder="DESC"
                                showInvitation={false}
                            />
                            <DateField
                                source="public.validfrom"
                                label="resources.teacher.fields.validfrom"
                                locales="fi-FI"
                                showTime={false}
                            />
                            <DateField
                                source="public.validto"
                                label="resources.teacher.fields.validto"
                                locales="fi-FI"
                                showTime={false}
                            />
                            <CustomLogAsTeacherButton /* sortable={sortable.includes('log_as_teacher')} */
                                label={translate(
                                    'actions.login_as.column_label'
                                )}
                            />
                            <CustomCopyAccessTokenButton
                                role="teacher"
                                source="copy_access_token"
                                label={translate(
                                    'actions.copy_access_token.column_label'
                                )}
                            />
                            <SourceNameField label="Lähde" size="small" />
                        </DatagridConfigurable>
                    )
                }
            />
        </List>
    );
};

export default TeacherList;
